import { observer } from 'mobx-react-lite'
import { Typography } from 'shared/ui'
import { makeTextFieldIcon } from 'shared/ui/TextField'
import { SendFrom } from 'widgets/SendFrom'
import { useRcsContext } from 'pages/settings/pages/rcs/context/rcsContext'
import { RcsInfoFormRow } from './RcsInfoFormRow'

import styles from './styles.module.scss'

interface IRcsInfoFormSectionProps {
  section: 'general' | 'colorsAndImages' | 'contact'
}

export const RcsInfoFormSection = observer(({ section }: IRcsInfoFormSectionProps) => {
  const { rcs, sendFromStore } = useRcsContext()

  if (!rcs) return null

  if (section === 'general') {
    return (
      <section className={styles.container}>
        <div className={styles.header}>
          <Typography variant={'body-md-medium'} ariaLabel={'RcsInfoFormHeaderGeneral'}>
            General info
          </Typography>
        </div>
        <div className={styles.content}>
          <RcsInfoFormRow label='Name'>
            <Typography
              variant={'text-input-field-r'}
              ariaLabel={'RcsInfoFormRowName'}
              color='var(--content-primary-primary)'
            >
              {rcs.name}
            </Typography>
          </RcsInfoFormRow>
          <RcsInfoFormRow label='Description'>
            <Typography
              variant={'text-input-field-r'}
              ariaLabel={'RcsInfoFormRowDescription'}
              color='var(--content-primary-primary)'
            >
              {rcs.description}
            </Typography>
          </RcsInfoFormRow>
          <RcsInfoFormRow
            label='Fallback'
            tooltipProps={{
              label: (
                <>
                  Some devices don’t support RCS.
                  <br />
                  Add a fallback inbox to ensure your
                  <br />
                  messages get delivered.
                </>
              ),
            }}
          >
            <SendFrom
              triggerVariant='textField'
              store={sendFromStore}
              width={418}
              textFiledTriggerProps={{
                textFieldPlaceholderProps: {
                  variant: 'stroke',
                  size: 'small',
                  className: styles.textField,
                  rightActions: [
                    makeTextFieldIcon({
                      icon: 'chevronDown',
                    }),
                  ],
                },
              }}
            />
          </RcsInfoFormRow>
        </div>
      </section>
    )
  }

  if (section === 'colorsAndImages') {
    return (
      <section className={styles.container}>
        <div className={styles.header}>
          <Typography variant={'body-md-medium'} ariaLabel={'RcsInfoFormHeaderColorsAndImages'}>
            Colors and images
          </Typography>
        </div>
        <div className={styles.content}>
          <RcsInfoFormRow label=' Logo' copyValue={rcs.origin.logo_path || ''}>
            <div className={styles.rowValue}>
              <div
                className={styles.rowValueImage}
                style={{ backgroundImage: `url(${rcs.origin.logo_path})` }}
              />
              <Typography
                variant={'text-input-field-r'}
                ariaLabel={'RcsInfoFormRowDescription'}
                color='var(--content-primary-primary)'
                ellipsis
                width={330}
              >
                {rcs.logoName}
              </Typography>
            </div>
          </RcsInfoFormRow>
          <RcsInfoFormRow label=' Banner' copyValue={rcs.origin.banner_path || ''}>
            <div className={styles.rowValue}>
              <div
                className={styles.rowValueImage}
                style={{
                  backgroundImage: `url(${rcs.origin.banner_path})`,
                }}
              />

              <Typography
                variant={'text-input-field-r'}
                ariaLabel={'RcsInfoFormRowDescription'}
                color='var(--content-primary-primary)'
                width={330}
                ellipsis
              >
                {rcs.bannerName}
              </Typography>
            </div>
          </RcsInfoFormRow>
          <RcsInfoFormRow label='Accent color'>
            <div className={styles.rowValue}>
              <div
                className={styles.rowValueColorWrapper}
                style={{ borderColor: rcs.origin.accent_color || '' }}
              >
                <div
                  className={styles.rowValueColor}
                  style={{ backgroundColor: rcs.origin.accent_color || '' }}
                ></div>
              </div>
              <Typography
                variant={'text-input-field-r'}
                ariaLabel={'RcsInfoFormRowDescription'}
                color='var(--content-primary-primary)'
              >
                {rcs.origin.accent_color}
              </Typography>
            </div>
          </RcsInfoFormRow>
        </div>
      </section>
    )
  }

  if (section === 'contact') {
    return (
      <section className={styles.container}>
        <div className={styles.header}>
          <Typography variant={'body-md-medium'} ariaLabel={'RcsInfoFormHeaderContact'}>
            Contact info
          </Typography>
        </div>
        <div className={styles.content}>
          {rcs.origin.contact_phones?.map((phone) => (
            <RcsInfoFormRow label={phone.label} key={phone.phone_number}>
              <Typography
                variant={'text-input-field-r'}
                ariaLabel={'RcsInfoFormRowDescription'}
                color='var(--content-primary-primary)'
              >
                {phone.phone_number}
              </Typography>
            </RcsInfoFormRow>
          ))}
          {rcs.origin.contact_emails?.map((email) => (
            <RcsInfoFormRow label={email.label} key={email.email}>
              <Typography
                variant={'text-input-field-r'}
                ariaLabel={'RcsInfoFormRowDescription'}
                color='var(--content-primary-primary)'
              >
                {email.email}
              </Typography>
            </RcsInfoFormRow>
          ))}
          {rcs.origin.website_urls?.map((website) => (
            <RcsInfoFormRow label={website.label} key={website.website}>
              <Typography
                variant={'text-input-field-r'}
                ariaLabel={'RcsInfoFormRowDescription'}
                color='var(--content-primary-primary)'
              >
                <a
                  className={styles.link}
                  href={website.website || ''}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={(e) => e.stopPropagation()}
                >
                  {website.website}
                </a>
              </Typography>
            </RcsInfoFormRow>
          ))}
          <RcsInfoFormRow label='Privacy policy url'>
            <Typography
              variant={'text-input-field-r'}
              ariaLabel={'RcsInfoFormRowDescription'}
              color='var(--content-primary-primary)'
            >
              <a
                className={styles.link}
                href={rcs.origin.privacy_policy_url || ''}
                target='_blank'
                rel='noopener noreferrer'
                onClick={(e) => e.stopPropagation()}
              >
                {rcs.origin.privacy_policy_url}
              </a>
            </Typography>
          </RcsInfoFormRow>
          <RcsInfoFormRow label='Terms and conditions url'>
            <Typography
              variant={'text-input-field-r'}
              ariaLabel={'RcsInfoFormRowDescription'}
              color='var(--content-primary-primary)'
            >
              <a
                className={styles.link}
                href={rcs.origin.terms_url || ''}
                target='_blank'
                rel='noopener noreferrer'
                onClick={(e) => e.stopPropagation()}
              >
                {rcs.origin.terms_url}
              </a>
            </Typography>
          </RcsInfoFormRow>
        </div>
      </section>
    )
  }

  return null
})
